import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { sdk } from '@/lib/sdk'
import { apiErrorText, apiFieldErrors, type MemberFieldValue } from '@/lib/sdk-official/resources/member-import'
import { MemberFieldsForm } from './MemberFieldsForm'
import { apiValues, mergeFields, missingRequired, valuesOf, type FieldWithValue } from './memberFields'
import { setCompleteProfileOpen } from './completeProfileState'
import { useWelcomeActive, useWelcomeRound } from '@/components/features/welcome/welcomeState'

/* Pages someone reaches from an e-mail link: a modal there would stand between
   the person and the one thing the link is for (setting a password, declining). */
const EXEMPT_PREFIXES = ['/_/auth/', '/_/invite/']

/**
 * F-85 decision 6: a required custom field the member has not filled in asks
 * for it on login and on entering a community, and the person goes on only
 * after filling it. `GET /auth/me` answers `missingRequiredFields` for the
 * community in `X-CommunityId` (and per profile in `profiles[]`).
 */
export const CompleteProfileModal = () => {
  const { t } = useTranslation('members')
  const { user, activeMember, isRoleResolved, mustChangePassword, logout } = useAuth()
  const { community } = useCommunity()
  const { currentPath } = useRoute()
  const [fields, setFields] = React.useState<FieldWithValue[]>([])
  const [values, setValues] = React.useState<Record<string, string>>({})
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [error, setError] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  /* F-155: the welcome comes first; when it ends, read again (its screens may have filled what was missing). */
  const welcomeActive = useWelcomeActive()
  const welcomeRound = useWelcomeRound()

  const memberId = activeMember?.id
  React.useEffect(() => {
    setFields([])
    if (!user?.id || !isRoleResolved || !memberId) return
    let alive = true
    sdk.auth.me()
      .then((me) => {
        const answer = me as unknown as {
          missingRequiredFields?: MemberFieldValue[]
          profiles?: { id: string; missingRequiredFields?: MemberFieldValue[] }[]
        }
        const missing = answer.missingRequiredFields
          ?? answer.profiles?.find((p) => p.id === memberId)?.missingRequiredFields
          ?? []
        if (!alive || !missing.length) return
        const merged = mergeFields(null, missing.map((f) => ({ ...f, value: null, required: true })))
        setFields(merged)
        setValues(valuesOf(merged))
        setErrors({})
        setError('')
      })
      .catch(() => { /* No answer, no modal: the next login asks again. */ })
    return () => { alive = false }
  }, [user?.id, isRoleResolved, memberId, community?.id, welcomeRound])

  const exempt = EXEMPT_PREFIXES.some((prefix) => (currentPath || '').startsWith(prefix))
  const open = fields.length > 0 && !mustChangePassword && !exempt && !welcomeActive
  React.useEffect(() => {
    setCompleteProfileOpen(open)
    return () => setCompleteProfileOpen(false)
  }, [open])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!memberId) return
    const missing = missingRequired(fields, values, (label) => t('fieldsSection.requiredError', { label }))
    setErrors(missing)
    if (Object.keys(missing).length) return
    setSaving(true)
    setError('')
    try {
      await sdk.memberFields.setMemberValues(memberId, apiValues(fields, values))
      setFields([])
    } catch (saveError) {
      setErrors(apiFieldErrors(saveError))
      setError(apiErrorText(saveError, t('fieldsSection.saveError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6 [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="complete-profile-modal"
      >
        <form onSubmit={save} className="min-w-0 space-y-4">
          <DialogHeader>
            <DialogTitle>{t('completeProfile.title')}</DialogTitle>
            <DialogDescription>{t('completeProfile.description', { community: community?.name ?? '' })}</DialogDescription>
          </DialogHeader>
          <MemberFieldsForm fields={fields} values={values} onChange={setValues} disabled={saving} errors={errors} className="sm:grid-cols-1" />
          {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" className="w-full sm:w-auto" disabled={saving} onClick={logout}>
              {t('completeProfile.signOut')}
            </Button>
            <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? t('completeProfile.saving') : t('completeProfile.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
